import { useState, useEffect } from "react";
import {
  getDownloadURL,
  listAll,
  ref,
  uploadBytesResumable,
  uploadBytes,
} from "firebase/storage";
import { doc, getDoc, updateDoc, setDoc, collection, getDocs } from "firebase/firestore";
import { storage, auth, db } from "../firebase";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { UploadCloud, Sparkles, XCircle, Grid, List} from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import imageCompression from 'browser-image-compression';
import { useNavigate } from "react-router-dom";
import { canUseStandardFeature } from "../utils/featureAccess";
import { Eye} from "lucide-react";
import { canAccessFeature } from "../utils/featureAccess";
function StyleUAI() {
  const [modelImages, setModelImages] = useState([]);
  const [modelPreviews, setModelPreviews] = useState([]);
  const [garmentImages, setGarmentImages] = useState([]);
  const [garmentPreviews, setGarmentPreviews] = useState([]);
  const [modelUploading, setModelUploading] = useState(false);
  const [garmentUploading, setGarmentUploading] = useState(false);
  const [modelUploadProgress, setModelUploadProgress] = useState({});
  const [garmentUploadProgress, setGarmentUploadProgress] = useState({});
  const [completedUploads, setCompletedUploads] = useState({});
  const [models, setModels] = useState([]);
  const [garments, setGarments] = useState([]);
  const [selectedModel, setSelectedModel] = useState("");
  const [selectedGarment, setSelectedGarment] = useState("");  
  const [userId, setUserId] = useState(null);
  const [credits, setCredits] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [showGenerationWindow, setShowGenerationWindow] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [userData, setUserData] = useState(null);
  
    const [previewImage, setPreviewImage] = useState(null);
  const navigate = useNavigate();
  
  // Pagination state with localStorage
  const [garmentType, setGarmentType] = useState("tops"); // default to tops
  const [modelPage, setModelPage] = useState(1);
  const [garmentPage, setGarmentPage] = useState(1);
  const imagesPerPage = 9;
  const [modelSearch, setModelSearch] = useState(localStorage.getItem('modelSearch') || "");
  const [garmentSearch, setGarmentSearch] = useState(localStorage.getItem('garmentSearch') || "");
  const [modelViewMode, setModelViewMode] = useState(localStorage.getItem('modelViewMode') || "grid");
  const [garmentViewMode, setGarmentViewMode] = useState(localStorage.getItem('garmentViewMode') || "grid");


  // Save state to localStorage
  useEffect(() => {
    localStorage.setItem('modelPage', modelPage.toString());
    localStorage.setItem('garmentPage', garmentPage.toString());
    localStorage.setItem('modelSearch', modelSearch);
    localStorage.setItem('garmentSearch', garmentSearch);
    localStorage.setItem('modelViewMode', modelViewMode);
    localStorage.setItem('garmentViewMode', garmentViewMode);
  }, [selectedModel, selectedGarment, modelPage, garmentPage, modelSearch, garmentSearch, modelViewMode, garmentViewMode]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const docRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const userInfo = docSnap.data();
          setUserId(user.uid);
          setUserData(userInfo);
  
          if (!canAccessFeature(userInfo, "styleuai")) {
            toast.error("StyleUAI is available only to subscribed users.");
            navigate("/pricing");
          }
        }
      } else {
        setUserId(null);
        toast.error("Please login to continue.");
        navigate("/");
      }
    });
  
    return () => unsubscribe();
  }, []);
  

  useEffect(() => {
    if (userId) {
      fetchUserCredits(userId);
      fetchImages(`assets/${userId}/models/`, setModels);
      fetchImages(`assets/${userId}/garments/`, setGarments);
    }
  }, [userId]);

  const fetchUserCredits = async (uid) => {
    const userRef = doc(db, "users", uid);
    const userSnap = await getDoc(userRef);
    if (userSnap.exists()) {
      setCredits(userSnap.data().credits);
    }
  };

const fetchImages = async (folder, setImages) => {
  if (!folder || !userId) return;

  const assetsRef = ref(storage, folder);

  try {
    const res = await listAll(assetsRef);

    // Fetch Firestore asset documents for better timestamp accuracy
    const assetDocsSnap = await getDocs(collection(db, "users", userId, "assets"));
    const assetDocs = {};
    assetDocsSnap.forEach(docSnap => {
      assetDocs[docSnap.id] = docSnap.data();
    });

    /* Build an array of { url, stamp } objects */
    const itemsWithStamp = await Promise.all(
      res.items.map(async (item) => {
        const url = await getDownloadURL(item);

        // Extract name without extension
        const imageNameWithoutExt = item.name.split(".")[0];
        
        // Get Firestore metadata if exists
        const firestoreData = assetDocs[imageNameWithoutExt] || {};

        // Use Firestore timestamp if available, otherwise fallback to filename timestamp
        let stamp = 0;
        if (firestoreData.timestamp) {
          stamp = firestoreData.timestamp;
        } else if (firestoreData.timeCreated) {
          stamp = firestoreData.timeCreated;
        } else {
          // Fallback: extract the 13‑digit millisecond stamp between first "_" and second "_"
          const match = item.name.match(/_(\d{13})_/);
          stamp = match ? Number(match[1]) : 0;
        }

        return { url, stamp };
      })
    );

    /* Newest first */
    itemsWithStamp.sort((a, b) => b.stamp - a.stamp);

    /* Keep only the URLs in sorted order */
    setImages(itemsWithStamp.map((i) => i.url));
  } catch (err) {
    console.error("Error fetching images:", err);
  }
};



const handleFileChange = async (event, setImages, setPreviews, currentImages = []) => {
  const files = Array.from(event.target.files);
  if (!files.length) return;

  // Check if adding these files would exceed the limit
  if (currentImages.length + files.length > 10) {
    toast.error("Maximum 10 images allowed. Please select fewer files.");
    return;
  }

  const validTypes = ["image/png", "image/jpeg", "image/jpg"];
  const maxSize = 10 * 1024 * 1024; // 10MB

  // Process each file and update state immediately when processed
  for (let i = 0; i < files.length; i++) {
    const file = files[i];

  if (!validTypes.includes(file.type)) {
      toast.error(`File ${file.name}: Only JPG, JPEG, and PNG files are allowed.`);
      continue;
    }

    if (file.size > maxSize) {
      toast.error(`File ${file.name}: Must be less than 10MB.`);
      continue;
  }

  const imageUrl = URL.createObjectURL(file);
  const img = new Image();

  img.onload = async () => {
    const { width, height } = img;

    if (width < 500 || height < 500) {
        toast.error(`File ${file.name}: Image resolution must be at least 500x500 pixels.`);
      return;
    }

    if (width > 3000 || height > 3000) {
      // Resize to fit within 3000x3000 while maintaining aspect ratio
      const scaleFactor = Math.min(3000 / width, 3000 / height);
      const newWidth = Math.round(width * scaleFactor);
      const newHeight = Math.round(height * scaleFactor);

      const canvas = document.createElement("canvas");
      canvas.width = newWidth;
      canvas.height = newHeight;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      canvas.toBlob((blob) => {
        if (!blob) {
            toast.error(`File ${file.name}: Failed to process resized image.`);
          return;
        }

        const resizedFile = new File([blob], file.name, {
          type: file.type,
          lastModified: Date.now(),
        });

        if (resizedFile.size > maxSize) {
            toast.error(`File ${file.name}: Resized image exceeds 10MB.`);
          return;
        }

          // Update state immediately for this file
          setImages(prev => [...prev, resizedFile]);
          setPreviews(prev => [...prev, URL.createObjectURL(resizedFile)]);
      }, file.type === "image/png" ? "image/png" : "image/jpeg", 1.0);
    } else {
        // Update state immediately for this file
        setImages(prev => [...prev, file]);
        setPreviews(prev => [...prev, imageUrl]);
    }
  };

  img.onerror = () => {
      toast.error(`File ${file.name}: Invalid image file.`);
  };

  img.src = imageUrl;
  }
};


  const handleDrop = (e, setImages, setPreviews, currentImages = []) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length) {
      handleFileChange({ target: { files } }, setImages, setPreviews, currentImages);
    }
  };
  
  const handleDragOver = (e) => e.preventDefault();

  const deductCredits = async (uid) => {
    const userRef = doc(db, "users", uid);
    const userSnap = await getDoc(userRef);
    const currentCredits = userSnap.data()?.credits || 0;
    
    await updateDoc(userRef, {
      credits: currentCredits - 5,
    });
    setCredits(currentCredits - 5);
  };

  const handleUpload = async (images, folder, setImages, setPreviews, type) => {
    if (!images.length || !userId) return;

  const toastId = `upload-success-${type}`;
  const errorToastId = `upload-error-${type}`;

  // Set uploading flags
  if (type === "model") setModelUploading(true);
  else setGarmentUploading(true);

    let successCount = 0;
    let errorCount = 0;

    // Upload files sequentially to avoid overwhelming the server
    for (let i = 0; i < images.length; i++) {
      const image = images[i];
  const filename = image.name;
  const filenameKey = filename.split(".")[0];
  const storagePath = `assets/${userId}/${folder}/${filename}`;
  const storageRef = ref(storage, storagePath);

      try {
        // Update progress for this specific file
        if (type === "model") {
          setModelUploadProgress(prev => ({ ...prev, [filename]: 0 }));
        } else {
          setGarmentUploadProgress(prev => ({ ...prev, [filename]: 0 }));
        }

  const uploadTask = uploadBytesResumable(storageRef, image);

        await new Promise((resolve, reject) => {
  uploadTask.on(
    "state_changed",
    (snapshot) => {
      const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              if (type === "model") {
                setModelUploadProgress(prev => ({ ...prev, [filename]: progress }));
              } else {
                setGarmentUploadProgress(prev => ({ ...prev, [filename]: progress }));
              }
    },
    (error) => {
              console.error(`Upload failed for ${filename}:`, error);
              errorCount++;
              reject(error);
    },
    async () => {
      try {
        const downloadURL = await getDownloadURL(storageRef);

                // Save download URL to Firestore
        const docRef = doc(db, "users", userId, "assets", filenameKey);
        await setDoc(docRef, {
          imageUrl: downloadURL,
          folder,
          type,
          timestamp: Date.now(),
        });

                successCount++;
                
                // Mark as completed and auto-hide after 2 seconds
                const completedKey = `${type}-${filename}`;
                setCompletedUploads(prev => ({ ...prev, [completedKey]: true }));
                
                setTimeout(() => {
                  setCompletedUploads(prev => {
                    const newState = { ...prev };
                    delete newState[completedKey];
                    return newState;
                  });
                }, 2000);
                
                resolve();
      } catch (err) {
                console.error(`Post-upload error for ${filename}:`, err);
                errorCount++;
                reject(err);
              }
            }
          );
        });
      } catch (error) {
        console.error(`Failed to upload ${filename}:`, error);
        errorCount++;
      }
    }

    // Refresh UI and show results
    await fetchImages(`assets/${userId}/${folder}/`, setImages);
    setPreviews([]);

    if (successCount > 0) {
      toast.success(`${successCount} image(s) uploaded successfully!`, { toastId });
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} image(s) failed to upload.`, { toastId: errorToastId });
    }

    // Reset uploading flags
    if (type === "model") {
      setModelUploading(false);
      setModelUploadProgress({});
    } else {
      setGarmentUploading(false);
      setGarmentUploadProgress({});
    }
};

  
  
const toggleSelectModel = (url) => {
  setSelectedModel(selectedModel === url ? "" : url);
};

const toggleSelectGarment = (url) => {
  setSelectedGarment(selectedGarment === url ? "" : url);
};


const handleGenerate = async () => {

   // 🔑 Credits check using state (no async fetch)
   const requiredCredits = 5;
   if (credits < requiredCredits) {
     toast.error("Insufficient credits. Buy more.");
     return; // 🚫 stop here, no async queued
   }

  if (!selectedModel || !selectedGarment) {
    toast.error("Please select both a model and a garment.");
    return;
  }

  if (!selectedModel.startsWith("http") || !selectedGarment.startsWith("http")) {
    toast.error("Selected image links are invalid. Please re-upload.");
    return;
  }

  if (modelUploading || garmentUploading) {
    toast.error("Please wait for the image upload to finish.");
    return;
  }

  if (!userId) {
    toast.error("User not authenticated. Please log in again.");
    return;
  }

 

  // ✅ Only proceed if credits are fine
  setGeneratedImage(null);
  setShowGenerationWindow(true);
  setGenerating(true);
  setGenerationProgress(0);

  // Progress simulation
  const progressInterval = setInterval(() => {
    setGenerationProgress((prev) => {
      if (prev >= 90) {
        clearInterval(progressInterval);
        return prev;
      }
      return prev + 10;
    });
  }, 1000);

  try {
    const response = await fetch("https://api.styleuai.com/tryon/generate", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        model_image_url: selectedModel,
        garment_image_url: selectedGarment,
        garment_type: garmentType,
      }),
    });

    const data = await response.json();
    if (!response.ok || !data.result_url) {
      throw new Error(data.error || "Server failed to return image.");
    }

    // Save generated image to Firebase Storage
    const blob = await (await fetch(data.result_url)).blob();
    const filename = `StyleUAI-${Date.now()}.png`;
    const storageRef = ref(storage, `assets/${userId}/outputs/${filename}`);
    await uploadBytes(storageRef, blob);
    const downloadURL = await getDownloadURL(storageRef);

    // Save metadata in Firestore
    const outputRef = doc(db, "users", userId, "assets", `output-${Date.now()}`);
    await setDoc(outputRef, {
      imageUrl: downloadURL,
      type: "Generated",
      timestamp: Date.now(),
    });

    // Deduct credits AFTER success
    await deductCredits(userId);

    setGeneratedImage(downloadURL);
    setGenerationProgress(100);
    toast.success("Generation successful! Saved in Outputs.");
  } catch (error) {
    console.error("Generation error:", error);
    toast.error("Failed to generate image.");
  } finally {
    setGenerating(false);
    clearInterval(progressInterval);
  }
};


  // Filter models and garments based on search
  const filteredModels = models.filter(model => 
    model.toLowerCase().includes(modelSearch.toLowerCase())
  );
  const filteredGarments = garments.filter(garment => 
    garment.toLowerCase().includes(garmentSearch.toLowerCase())
  );

  // Calculate pagination
  const modelPageCount = Math.ceil(filteredModels.length / imagesPerPage);
  const garmentPageCount = Math.ceil(filteredGarments.length / imagesPerPage);

  // Get paginated items
  const paginatedModels = filteredModels.slice(
    (modelPage - 1) * imagesPerPage,
    modelPage * imagesPerPage
  );
  const paginatedGarments = filteredGarments.slice(
    (garmentPage - 1) * imagesPerPage,
    garmentPage * imagesPerPage
  );

  // Save pagination state to localStorage
  useEffect(() => {
    localStorage.setItem('modelPage', modelPage.toString());
    localStorage.setItem('garmentPage', garmentPage.toString());
  }, [modelPage, garmentPage]);

  // Smart pagination helper function
  const getPaginationNumbers = (currentPage, totalPages, maxVisible = 5) => {
    const pages = [];
    
    if (totalPages <= maxVisible) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      if (currentPage <= 3) {
        // Show first few pages
        for (let i = 2; i <= Math.min(4, totalPages - 1); i++) {
          pages.push(i);
        }
        if (totalPages > 5) {
          pages.push('...');
        }
      } else if (currentPage >= totalPages - 2) {
        // Show last few pages
        if (totalPages > 5) {
          pages.push('...');
        }
        for (let i = Math.max(totalPages - 3, 2); i <= totalPages - 1; i++) {
          pages.push(i);
        }
      } else {
        // Show pages around current page
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
      }
      
      // Always show last page (if more than 1 page)
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  return (
    <div className="flex h-screen">
      <div className="hidden md:block">
    <Sidebar />
  </div>
      <div className="flex flex-col flex-1 bg-white overflow-x-hidden">
       <div className="absolute top-2 md:top-4 right-2 md:right-4">
    <Header />
  </div>

<div className="block md:hidden">
    <Sidebar />
  </div>
<div className="content-container">
        <div className="p-2 sm:p-4 md:p-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-800">StyleUAI</h2>
                <p className="text-gray-500 text-sm md:text-base mt-1">Get your DIY model-ready visuals.</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="bg-[#F7F5FF] px-4 py-2 rounded-lg">
                  <span className="text-gray-600">Credits:</span>
                  <span className="ml-2 font-semibold text-[#3913C5]">{credits}</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Model Section */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-800">Model Image</h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setModelViewMode("grid")}
                      className={`p-2 rounded-md ${
                        modelViewMode === "grid" ? "bg-[#3913C5] text-white" : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      <Grid className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setModelViewMode("list")}
                      className={`p-2 rounded-md ${
                        modelViewMode === "list" ? "bg-[#3913C5] text-white" : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      <List className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Upload Section */}
                <div className="mb-6">
                <label
                  htmlFor="model-upload"
                  className="block w-full"
                  onDrop={(e) => handleDrop(e, setModelImages, setModelPreviews, modelImages)}
                  onDragOver={handleDragOver}
                >
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-[#3913C5] transition-colors">
                    {modelPreviews.length > 0 ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                          {modelPreviews.map((preview, index) => (
                            <div key={index} className="relative">
                              <img src={preview} alt={`Preview ${index + 1}`} className="w-full h-24 object-contain rounded-lg" />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                                  const newImages = modelImages.filter((_, i) => i !== index);
                                  const newPreviews = modelPreviews.filter((_, i) => i !== index);
                                  setModelImages(newImages);
                                  setModelPreviews(newPreviews);
                                }}
                                className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                              >
                                <XCircle className="w-3 h-3" />
                        </button>
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500">
                          {modelImages.length}/10 images selected
                        </p>
                      </div>
                    ) : (
                      <div className="py-8">
                        <UploadCloud className="mx-auto text-gray-400 mb-2" size={24} />
                        <p className="text-sm text-gray-500">Click or drag and drop to upload model images</p>
                        <p className="text-xs text-gray-400 mt-1">JPG, PNG • Max 10MB each • Up to 10 images</p>
                      </div>
                    )}
                  </div>
                </label>

                <input
                  id="model-upload"
                  type="file"
                  accept="image/png, image/jpeg, image/jpg"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFileChange(e, setModelImages, setModelPreviews, modelImages)}
                />
              </div>

              {/* Model Upload */}
              <button
                onClick={() => handleUpload(modelImages, "models", setModels, setModelPreviews, "model")}
                className="mt-3 w-full bg-[#3913C5] text-white py-2 px-4 rounded-lg disabled:opacity-50 hover:bg-[#2a0d8f] transition-colors"
                disabled={modelUploading || !userId || modelImages.length === 0}
              >
                {modelUploading ? "Uploading..." : "Upload"}
              </button>
              {modelUploading && (
                <div className="mt-3 space-y-2">
                  {Object.entries(modelUploadProgress).map(([filename, progress]) => {
                    const completedKey = `model-${filename}`;
                    const isCompleted = completedUploads[completedKey];
                    const isHidden = isCompleted && progress === 100;
                    
                    return (
                      <div 
                        key={filename} 
                        className={`transition-all duration-500 ${
                          isHidden ? 'opacity-0 transform scale-95' : 'opacity-100 transform scale-100'
                        }`}
                        style={{ 
                          display: isHidden ? 'none' : 'block',
                          transition: 'all 0.5s ease-in-out'
                        }}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-gray-600 truncate max-w-[180px] font-medium">
                            {filename}
                          </span>
                          <div className="flex items-center gap-2">
                            {isCompleted ? (
                              <div className="flex items-center gap-1 text-green-600">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                <span className="text-xs font-medium">Complete</span>
                              </div>
                            ) : (
                              <span className="text-xs text-[#3913C5] font-medium">
                                {Math.round(progress)}%
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="relative w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-2 rounded-full transition-all duration-500 ease-out ${
                              isCompleted 
                                ? 'bg-gradient-to-r from-green-400 to-green-500' 
                                : 'bg-gradient-to-r from-[#3913C5] to-[#5B21B6]'
                            }`}
                            style={{ 
                              width: `${progress}%`,
                              boxShadow: isCompleted ? '0 0 8px rgba(34, 197, 94, 0.4)' : '0 0 8px rgba(57, 19, 197, 0.4)'
                            }}
                          >
                            {!isCompleted && (
                              <div className="absolute inset-0 bg-white opacity-30 animate-pulse"></div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

                {/* Model Grid/List */}
                <div className="border-t pt-4">
                  {modelViewMode === "grid" ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">

                      {paginatedModels.map((url, index) => (
                        <div
                          key={index}
                          className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer transition-all ${
                            selectedModel === url ? "ring-2 ring-[#3913C5]" : "hover:ring-2 hover:ring-gray-200"
                          }`}
                          onClick={() => toggleSelectModel(url)}
                        >
                          <img
                            src={url}
                            alt="Model"
                            className="w-full h-full object-contain"
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {paginatedModels.map((url, index) => (
                        <div
                          key={index}
                          className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                            selectedModel === url ? "bg-[#F7F5FF]" : "hover:bg-gray-50"
                          }`}
                          onClick={() => toggleSelectModel(url)}
                        >
                          <div className="w-12 h-12 rounded overflow-hidden">
                            <img src={url} alt="Model" className="w-full h-full object-cover" />
                          </div>
                          <span className="text-sm text-gray-600">Model {index + 1}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Model Pagination */}
                  {modelPageCount > 1 && (
                    <div className="flex justify-center mt-4 space-x-2 flex-wrap">
                      <button
                        onClick={() => setModelPage((p) => Math.max(1, p - 1))}
                        disabled={modelPage === 1}
                        className="px-3 py-1 bg-[#3913C5] text-white rounded-md disabled:opacity-50 hover:bg-[#2a0d8f] transition-colors"
                      >
                        Previous
                      </button>
                      {getPaginationNumbers(modelPage, modelPageCount).map((number, index) => (
                          <button
                          key={index}
                          onClick={() => typeof number === 'number' && setModelPage(number)}
                          disabled={number === '...'}
                            className={`px-3 py-1 rounded-md ${
                              modelPage === number
                                ? "bg-[#3913C5] text-white"
                              : number === '...'
                              ? "bg-transparent text-gray-400 cursor-default"
                                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            }`}
                          >
                            {number}
                          </button>
                      ))}
                      <button
                        onClick={() => setModelPage((p) => Math.min(modelPageCount, p + 1))}
                        disabled={modelPage === modelPageCount}
                        className="px-3 py-1 bg-[#3913C5] text-white rounded-md disabled:opacity-50 hover:bg-[#2a0d8f] transition-colors"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Garment Section */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-800">Garment Image</h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setGarmentViewMode("grid")}
                      className={`p-2 rounded-md ${
                        garmentViewMode === "grid" ? "bg-[#3913C5] text-white" : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      <Grid className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setGarmentViewMode("list")}
                      className={`p-2 rounded-md ${
                        garmentViewMode === "list" ? "bg-[#3913C5] text-white" : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      <List className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Upload Section */}
                <div className="mb-6">
                <label
                  htmlFor="garment-upload"
                  className="block w-full"
                  onDrop={(e) => handleDrop(e, setGarmentImages, setGarmentPreviews, garmentImages)}
                  onDragOver={handleDragOver}
                >
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-[#3913C5] transition-colors">
                    {garmentPreviews.length > 0 ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                          {garmentPreviews.map((preview, index) => (
                            <div key={index} className="relative">
                              <img src={preview} alt={`Preview ${index + 1}`} className="w-full h-24 object-contain rounded-lg" />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                                  const newImages = garmentImages.filter((_, i) => i !== index);
                                  const newPreviews = garmentPreviews.filter((_, i) => i !== index);
                                  setGarmentImages(newImages);
                                  setGarmentPreviews(newPreviews);
                                }}
                                className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                              >
                                <XCircle className="w-3 h-3" />
                        </button>
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500">
                          {garmentImages.length}/10 images selected
                        </p>
                      </div>
                    ) : (
                      <div className="py-8">
                        <UploadCloud className="mx-auto text-gray-400 mb-2" size={24} />
                        <p className="text-sm text-gray-500">Click or drag and drop to upload garment images</p>
                        <p className="text-xs text-gray-400 mt-1">JPG, PNG • Max 10MB each • Up to 10 images</p>
                      </div>
                    )}
                  </div>
                </label>

                <input
                  id="garment-upload"
                  type="file"
                  accept="image/png, image/jpeg, image/jpg"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFileChange(e, setGarmentImages, setGarmentPreviews, garmentImages)}
                />
              </div>

              {/* Garment Upload */}
              <button
                onClick={() => handleUpload(garmentImages, "garments", setGarments, setGarmentPreviews, "garment")}
                className="mt-3 w-full bg-[#3913C5] text-white py-2 px-4 rounded-lg disabled:opacity-50 hover:bg-[#2a0d8f] transition-colors"
                disabled={garmentUploading || !userId || garmentImages.length === 0}
              >
                {garmentUploading ? "Uploading..." : "Upload"}
              </button>
              {garmentUploading && (
                <div className="mt-3 space-y-2">
                  {Object.entries(garmentUploadProgress).map(([filename, progress]) => {
                    const completedKey = `garment-${filename}`;
                    const isCompleted = completedUploads[completedKey];
                    const isHidden = isCompleted && progress === 100;
                    
                    return (
                      <div 
                        key={filename} 
                        className={`transition-all duration-500 ${
                          isHidden ? 'opacity-0 transform scale-95' : 'opacity-100 transform scale-100'
                        }`}
                        style={{ 
                          display: isHidden ? 'none' : 'block',
                          transition: 'all 0.5s ease-in-out'
                        }}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-gray-600 truncate max-w-[180px] font-medium">
                            {filename}
                          </span>
                          <div className="flex items-center gap-2">
                            {isCompleted ? (
                              <div className="flex items-center gap-1 text-green-600">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                <span className="text-xs font-medium">Complete</span>
                              </div>
                            ) : (
                              <span className="text-xs text-[#3913C5] font-medium">
                                {Math.round(progress)}%
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="relative w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-2 rounded-full transition-all duration-500 ease-out ${
                              isCompleted 
                                ? 'bg-gradient-to-r from-green-400 to-green-500' 
                                : 'bg-gradient-to-r from-[#3913C5] to-[#5B21B6]'
                            }`}
                            style={{ 
                              width: `${progress}%`,
                              boxShadow: isCompleted ? '0 0 8px rgba(34, 197, 94, 0.4)' : '0 0 8px rgba(57, 19, 197, 0.4)'
                            }}
                          >
                            {!isCompleted && (
                              <div className="absolute inset-0 bg-white opacity-30 animate-pulse"></div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

                {/* Garment Grid/List */}
                <div className="border-t pt-4">
                  {garmentViewMode === "grid" ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {paginatedGarments.map((url, index) => (
                        <div
                          key={index}
                          className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer transition-all ${
                            selectedGarment === url ? "ring-2 ring-[#3913C5]" : "hover:ring-2 hover:ring-gray-200"
                          }`}
                          onClick={() => toggleSelectGarment(url)}
                        >
                          <img
                            src={url}
                            alt="Garment"
                            className="w-full h-full object-contain"
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {paginatedGarments.map((url, index) => (
                        <div
                          key={index}
                          className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                            selectedGarment === url ? "bg-[#F7F5FF]" : "hover:bg-gray-50"
                          }`}
                          onClick={() => toggleSelectGarment(url)}
                        >
                          <div className="w-12 h-12 rounded overflow-hidden">
                            <img src={url} alt="Garment" className="w-full h-full object-cover" />
                          </div>
                          <span className="text-sm text-gray-600">Garment {index + 1}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Garment Pagination */}
                  {garmentPageCount > 1 && (
                    <div className="flex justify-center mt-4 space-x-2 flex-wrap">
                      <button
                        onClick={() => setGarmentPage((p) => Math.max(1, p - 1))}
                        disabled={garmentPage === 1}
                        className="px-3 py-1 bg-[#3913C5] text-white rounded-md disabled:opacity-50 hover:bg-[#2a0d8f] transition-colors"
                      >
                        Previous
                      </button>
                      {getPaginationNumbers(garmentPage, garmentPageCount).map((number, index) => (
                          <button
                          key={index}
                          onClick={() => typeof number === 'number' && setGarmentPage(number)}
                          disabled={number === '...'}
                            className={`px-3 py-1 rounded-md ${
                              garmentPage === number
                                ? "bg-[#3913C5] text-white"
                              : number === '...'
                              ? "bg-transparent text-gray-400 cursor-default"
                                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            }`}
                          >
                            {number}
                          </button>
                      ))}
                      <button
                        onClick={() => setGarmentPage((p) => Math.min(garmentPageCount, p + 1))}
                        disabled={garmentPage === garmentPageCount}
                        className="px-3 py-1 bg-[#3913C5] text-white rounded-md disabled:opacity-50 hover:bg-[#2a0d8f] transition-colors"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Garment Type</label>
            <select
              value={garmentType}
              onChange={(e) => setGarmentType(e.target.value)}
              className="border rounded-lg p-2 w-full"
            >
              <option value="tops">Upper Wear</option>
              <option value="bottoms">Lower Wear</option>
              <option value="dresses">One-Piece</option>
            </select>
          </div>


            {/* Generate Button */}
            <div className="mt-8 text-center">
              <button
                onClick={handleGenerate}
                className="bg-[#3913C5] text-white py-3 px-8 rounded-lg disabled:opacity-50 hover:bg-[#2a0d8f] transition-colors flex items-center gap-2 mx-auto"
                disabled={!selectedModel || !selectedGarment || generating}
              >
                <Sparkles className="w-5 h-5" />
                {generating ? "Generating..." : "Generate"}
              </button>
            </div>
          </div>

          {/* Generation Window Modal */}
        {showGenerationWindow && (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
        <div className="bg-white rounded-2xl shadow-xl max-w-6xl w-full p-4 sm:p-6 m-4">
          {/* Header */}
          <div className="flex justify-between items-center mb-4 sm:mb-6">
            <h3 className="text-lg sm:text-xl font-semibold">Generation Progress</h3>
            <button
              onClick={() => setShowGenerationWindow(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <XCircle className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>

          {/* Previews */}
         <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-6">
            {/* Model Preview */}
          <div className="bg-gray-50 rounded-xl p-3 sm:p-4">
            <h4 className="text-xs sm:text-sm font-medium text-gray-500 mb-2">Model</h4>
            <div className="rounded-lg overflow-hidden relative group cursor-pointer">
              <img src={selectedModel} alt="Selected Model" className="w-full h-auto max-h-[50vh] object-contain rounded-lg" />
              <div
                className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center"
                onClick={() => setPreviewImage(selectedModel)}
              >
                <Eye className="text-white w-6 h-6 sm:w-8 sm:h-8" />
              </div>
            </div>
          </div>

{/* Garment Preview */}
<div className="bg-gray-50 rounded-xl p-3 sm:p-4">
  <h4 className="text-xs sm:text-sm font-medium text-gray-500 mb-2">Garment</h4>
  <div className="rounded-lg overflow-hidden relative group cursor-pointer">
    <img src={selectedGarment} alt="Selected Garment" className="w-full h-auto max-h-[50vh] object-contain rounded-lg" />
    <div
      className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center"
      onClick={() => setPreviewImage(selectedGarment)}
    >
      <Eye className="text-white w-6 h-6 sm:w-8 sm:h-8" />
    </div>
  </div>
</div>

{/* Generated Image Preview */}
{/* Generated Image Preview */}
<div className="bg-gray-50 rounded-xl p-3 sm:p-4">
  <h4 className="text-xs sm:text-sm font-medium text-gray-500 mb-2">Generated</h4>
  <div className="rounded-lg overflow-hidden relative group cursor-pointer">
    {generatedImage ? (
      <>
        <img
          src={generatedImage}
          alt="Generated Image"
          className="w-full h-auto max-h-[70vh] object-contain rounded-lg"
        />
        <div
          className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center"
          onClick={() => setPreviewImage(generatedImage)}
        >
          <Eye className="text-white w-6 h-6 sm:w-8 sm:h-8" />
        </div>
      </>
    ) : (
      <div className="relative min-h-[300px] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#3913C5] border-t-transparent" />
      </div>
    )}
  </div>
</div>

          </div>

          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs sm:text-sm font-medium text-gray-600">
                {generating ? "Generating..." : "Complete"}
              </span>
              <span className="text-xs sm:text-sm font-medium text-[#3913C5]">
                {generationProgress}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-[#3913C5] h-2 rounded-full transition-all duration-300"
                style={{ width: `${generationProgress}%` }}
              ></div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-4">
            <button
              onClick={() => {setShowGenerationWindow(false);    
              setGeneratedImage(null);
            }}
              className="w-full sm:w-auto px-3 sm:px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
            >
              Close
            </button>
            {generatedImage && (
              <button
                onClick={() => {
                  setShowGenerationWindow(false);
                  navigate("/outputs");
                  setGeneratedImage(null);
                }}
                className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-[#3913C5] text-white rounded-xl hover:bg-[#2a0d8f] transition-colors"
              >
                View in Outputs
              </button>
            )}
          </div>
        </div>
      </div>
    )}

    {/* Image Preview Modal */}
    {previewImage && (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl p-4 max-w-3xl w-full">
          <button
            onClick={() => setPreviewImage(null)}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          >
            <XCircle className="w-6 h-6" />
          </button>
          <img
            src={previewImage}
            alt="Preview"
            className="w-full h-auto max-h-[80vh] object-contain rounded-xl"
          />
        </div>
      </div>
    )}
</div>
</div>
</div>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
}

export default StyleUAI;